import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faMoneyBillWave,
    faCheckCircle,
    faClock,
    faSpinner,
    faChevronRight,
    faCalendarAlt
} from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';
import { useAuth } from '../../contexts/AuthContext';

const SectionReviewDashboard = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [pendingSections, setPendingSections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchPendingSections();
    }, []);

    const fetchPendingSections = async () => {
        try {
            setLoading(true);
            const response = await api.get('/payments/my-sections');
            if (response.data.success) {
                setPendingSections(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching pending sections:', err);
            setError('Erreur lors du chargement des sections en attente');
        } finally {
            setLoading(false);
        }
    };

    const getSectionLabel = (section) => {
        const labels = {
            cati: 'CATI',
            f2f: 'F2F / Terrain',
            controller: 'Contrôleurs',
        };
        return labels[section] || section;
    };

    const getSectionColor = (section) => {
        const colors = {
            cati: 'bg-purple-100 text-purple-700 border-purple-200',
            f2f: 'bg-green-100 text-green-700 border-green-200',
            controller: 'bg-blue-100 text-blue-700 border-blue-200',
        };
        return colors[section] || 'bg-gray-100 text-gray-700 border-gray-200';
    };

    return (
        <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
            <div className="max-w-5xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
                        VALIDATION DES SECTIONS
                    </h1>
                    <p className="text-gray-600">
                        Lots de paiement en attente de votre validation
                    </p>
                </div>

                {/* Error */}
                {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
                        {error}
                    </div>
                )}

                {/* Loading */}
                {loading ? (
                    <div className="bg-white rounded-lg shadow-sm p-12 text-center border border-gray-200">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
                        <p className="text-gray-600">Chargement...</p>
                    </div>
                ) : pendingSections.length === 0 ? (
                    <div className="bg-white rounded-lg shadow-sm p-12 text-center border border-gray-200">
                        <FontAwesomeIcon icon={faCheckCircle} className="text-6xl text-green-300 mb-4" />
                        <p className="text-gray-600 text-lg">Aucune section en attente de validation</p>
                        <p className="text-gray-500 text-sm mt-2">Vous serez notifié lorsqu'un nouveau lot nécessite votre validation</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {pendingSections.map((item) => (
                            <div
                                key={`${item.batch_id}-${item.section}`}
                                onClick={() => navigate(`/payments/${item.batch_id}`)}
                                className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 hover:shadow-md hover:border-gray-300 cursor-pointer transition-all duration-200"
                            >
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-3 mb-2">
                                            <span className="text-lg font-semibold text-gray-900">
                                                Lot #{item.batch_id}
                                            </span>
                                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getSectionColor(item.section)}`}>
                                                {getSectionLabel(item.section)}
                                            </span>
                                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700 border border-yellow-200">
                                                <FontAwesomeIcon icon={faClock} className="mr-1" />
                                                En attente
                                            </span>
                                        </div>
                                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                                            <div className="flex items-center">
                                                <FontAwesomeIcon icon={faCalendarAlt} className="mr-1.5 text-gray-400" />
                                                {new Date(item.period_start).toLocaleDateString('fr-FR')} - {new Date(item.period_end).toLocaleDateString('fr-FR')}
                                            </div>
                                            <div className="flex items-center">
                                                <FontAwesomeIcon icon={faMoneyBillWave} className="mr-1.5 text-gray-400" />
                                                {item.section_lines_count} ligne{item.section_lines_count > 1 ? 's' : ''}
                                            </div>
                                            <div className="font-semibold text-green-600">
                                                {parseFloat(item.section_total).toFixed(2)} MAD
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex-shrink-0">
                                        <FontAwesomeIcon icon={faChevronRight} className="text-gray-400 text-lg" />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SectionReviewDashboard;
